const RECOVERY_KEY = "weft:vite-preload-recovery";

function recoveryId(revision: string): string {
  return `${revision}:${window.location.pathname}${window.location.search}`;
}

function readRecoveryId(): string | null | undefined {
  try {
    return sessionStorage.getItem(RECOVERY_KEY);
  } catch {
    return undefined;
  }
}

function writeRecoveryId(id: string): boolean {
  try {
    sessionStorage.setItem(RECOVERY_KEY, id);
    return true;
  } catch {
    return false;
  }
}

function clearRecoveryId(): boolean {
  try {
    sessionStorage.removeItem(RECOVERY_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Reload one stale page when a deploy has removed a lazy Vite chunk.
 *
 * A marker stops a broken current build from causing a reload loop. A newly
 * loaded revision or page clears an old marker. The same revision and page
 * keep it for their full lifetime, including React.lazy imports after mount.
 */
export function installPreloadRecovery(
  revision: string,
  reload: () => void = () => window.location.reload(),
): () => void {
  const initialId = recoveryId(revision);
  const previousId = readRecoveryId();

  // Storage can be unavailable by browser policy. In that case, leave the
  // original error visible instead of risking an unbounded reload loop.
  if (previousId === undefined) return () => {};
  if (previousId !== null && previousId !== initialId && !clearRecoveryId()) {
    return () => {};
  }

  const recover = (event: Event) => {
    const id = recoveryId(revision);
    if (readRecoveryId() === id || !writeRecoveryId(id)) return;

    event.preventDefault();
    reload();
  };

  window.addEventListener("vite:preloadError", recover);
  return () => window.removeEventListener("vite:preloadError", recover);
}
